import cx from 'clsx';
import { dateToLocalYMDHM } from 'common/date.ts';
import dayjs from 'common/dayjs.ts';
import { BaseError, errorCodes, errorMessages } from 'common/errors/index.ts';
import {
  computeDistance,
  GET,
  getActiveLeg,
  getAlertsToRead,
  getLegStatusColor,
  getNextETA,
  getRuntimeTimezone,
  getShipmentDelayedColor,
  getStatusColor,
  isGranted,
  isPastLegMissingPodDatetime,
  LEG_COLOR_STYLE,
  MPOST,
  MPUT,
  POST,
  statusToFill,
  stringToPosition,
  truncate,
  uniqueBy,
} from 'common/helpers.ts';
import { range } from 'es-toolkit';
import type { LatLngExpression } from 'leaflet';
import React from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, NavLink, useParams } from 'react-router-dom';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import type { Leg as TLeg } from 'types/Leg.ts';
import type { Package } from 'types/Package.ts';
import type { Shipment as TShipment } from 'types/Shipment.ts';
import type { UserSettings } from 'types/UserSettings.ts';
import { Button } from 'ui/component/Button.tsx';
import { CopyableText } from 'ui/component/CopyableText.tsx';
import { IBell, IBellSilent, ICamera, IClock, IEdit, IFileSpreadsheet, IHistory, IInfo, IRefresh, IWarning } from 'ui/component/Icons.tsx';
import { Spinner } from 'ui/component/Spinner.tsx';
import { Stepper } from 'ui/component/Stepper.tsx';
import { Tag } from 'ui/component/Tag.tsx';
import { Tooltip } from 'ui/component/Tooltip.tsx';
import axios from '#admin/axios.ts';
import { Datetime } from '#admin/component/Datetime.tsx';
import { useAlert, useConfirm } from '#admin/component/DialogProvider.tsx';
import { DocumentUploadDialog } from '#admin/component/DocumentUploadDialog.tsx';
import { DriverAssignmentDialog } from '#admin/component/DriverAssignmentDialog.tsx';
import { LegEditDialog } from '#admin/component/LegEditDialog.tsx';
import type { Marker, Polyline } from '#admin/component/Map.tsx';
import { MarkerNumber, MarkerPackage, MarkerSmartphone } from '#admin/component/markers.tsx';
import { PackageDetail } from '#admin/component/Package.tsx';
import { ReferenceIdDialog } from '#admin/component/ReferenceIdDialog.tsx';
import { ShipmentHistory } from '#admin/component/ShipmentHistory.tsx';
import { ShipmentInstructionsEditDialog } from '#admin/component/ShipmentInstructionsEditDialog.tsx';
import { SupplierAssignmentDialog } from '#admin/component/SupplierAssignmentDialog.tsx';
import { TrackerAssignmentDialog } from '#admin/component/TrackerAssignmentDialog.tsx';
import { canGeneratePublicLinkFromSession, isBridgeScopedSession } from '#admin/helpers.ts';
import { useEnv } from '#admin/hook/useEnv.tsx';
import { useLegPermissions } from '#admin/hook/useLegPermissions.tsx';
import { useMe } from '#admin/hook/useMe.tsx';
import { usePackagePermissions } from '#admin/hook/usePackagePermissions.tsx';
import { useXLSXShipments } from '#admin/hook/useXLSXShipments.tsx';
import type { RootState } from '#admin/store.ts';
import { Leg, LegDetailItem } from '../component/Leg.tsx';
import { isZeroPosition } from '../helpers.ts';

// This is the maximum distance in km that a package can be from the current leg's destination, otherwise it will be marked as red
const MAX_DISTANCE_M = 100;

// DataLogger column of the packages table is hidden for now — flip to true to bring it back along with the tracker assignment action
const SHOW_DATALOGGER_COLUMN: boolean = false;

// Column widths of the packages table, kept as literal strings so Tailwind can pick them up
const packagesGridCols = (bridgeMode: boolean, isAdminLayout: boolean): string => {
  if (SHOW_DATALOGGER_COLUMN) {
    if (bridgeMode) return isAdminLayout ? 'grid-cols-[1.5fr_40px_2fr_1fr_1.5fr_60px]' : 'grid-cols-[1fr_30px_2fr_30px]';
    return isAdminLayout ? 'grid-cols-[50px_1.5fr_40px_2fr_1fr_1.5fr_60px]' : 'grid-cols-[50px_1fr_30px_2fr_30px]';
  }
  if (bridgeMode) return isAdminLayout ? 'grid-cols-[1.5fr_40px_1fr_1.5fr_60px]' : 'grid-cols-[1fr_30px_30px]';
  return isAdminLayout ? 'grid-cols-[50px_1.5fr_40px_1fr_1.5fr_60px]' : 'grid-cols-[50px_1fr_30px_30px]';
};

// lazy load the map component
const MapComponent = React.lazy(() => import('../component/Map.tsx'));

export const ShipmentDetail: React.FC = () => {
  const { t } = useTranslation();
  const { me } = useMe();
  const env = useEnv();
  const confirm = useConfirm();
  const alert = useAlert();
  const token = useSelector((state: RootState) => state.app.token);
  const { id: shipmentId } = useParams();
  const isBridgeMode = isBridgeScopedSession(token);
  const canGeneratePublicLink = canGeneratePublicLinkFromSession(token);
  const { data: shipment, mutate: mutateShipment } = useSWR<TShipment>([`/shipment/${shipmentId}`], GET, { keepPreviousData: true });
  const [uploadDialog, setUploadDialog] = React.useState<TLeg[] | null>(null);
  const [legEditDialog, setLegEditDialog] = React.useState<TLeg | null>(null);
  const [trackerDialog, setTrackerDialog] = React.useState<Package | null>(null);
  const [driverDialog, setDriverDialog] = React.useState<TLeg | null>(null);
  const [supplierDialog, setSupplierDialog] = React.useState<TLeg | null>(null);
  const [packageDialog, setPackageDialog] = React.useState<Package | null>(null);
  const [historyDialog, setHistoryDialog] = React.useState<boolean>(false);
  const [referenceIdDialog, setReferenceIdDialog] = React.useState<boolean>(false);
  const [instructionsDialog, setInstructionsDialog] = React.useState<boolean>(false);
  const [defaultMapCenter, setDefaultMapCenter] = React.useState<LatLngExpression | undefined>(undefined);
  const [showOperatorButtons, setShowOperatorButtons] = React.useState<boolean>(false);
  const [generatedPublicUrl, setGeneratedPublicUrl] = React.useState<string | null>(null);
  const [generatedPublicLinkId, setGeneratedPublicLinkId] = React.useState<string | null>(null);
  const [copiedPublicLink, setCopiedPublicLink] = React.useState<boolean>(false);

  const { exportToXLSX } = useXLSXShipments(shipment ? [shipment] : []);

  useHotkeys('SHIFT + CTRL', () => {
    if (me?.group_role !== 'SUPERADMIN') return;
    setShowOperatorButtons(!showOperatorButtons);
  });
  const reassignShipmentMutation = useSWRMutation('/operator/reassign-shipments', MPOST);
  const { data: userSettings, mutate: mutateUserSettings } = useSWR<UserSettings>(isBridgeMode ? null : ['/user-settings'], GET);
  const setUserSettingsMutation = useSWRMutation('/user-settings', MPUT);
  const createPublicLinkMutation = useSWRMutation(`/shipments/${shipmentId}/public-visibility-links`, MPOST);
  const revokePublicLinkMutation = useSWRMutation(`/shipments/${shipmentId}/public-visibility-links`, async (url, { arg }: { arg: { linkId: string } }) => {
    const response = await axios.delete(`${url}/${arg.linkId}`);
    return response.data;
  });
  const { data: activePublicLink } = useSWR<{ id: string; public_url: string; expires_at: string | null } | null>(
    isBridgeMode && canGeneratePublicLink && shipmentId ? [`/shipments/${shipmentId}/public-visibility-links/active`] : null,
    GET,
  );

  // Use batch permissions for all legs in the shipment
  const { canAssignSupplier, canAssignDriver, canUpdateLeg } = useLegPermissions(isBridgeMode ? [] : shipment?.legs || [], ['LegAssignSupplier', 'LegAssignDriver', 'LegUpdate']);

  // Use batch permissions for all packages in the shipment
  const { canAssignTracker } = usePackagePermissions(isBridgeMode ? [] : shipment?.packages || [], ['TrackerAssign']);

  const geocodingIsMissing = React.useMemo(
    () => shipment?.legs.some((leg: TLeg) => !leg.origin_position || !leg.destination_position || isZeroPosition(leg.origin_position) || isZeroPosition(leg.destination_position)),
    [shipment],
  );

  const { alerts, alertsToRead } = React.useMemo(() => {
    if (!shipment) return { alerts: [], alertsToRead: [] };
    const alertsWithDupes = shipment.packages.flatMap((pkg: Package) => pkg.alerts);
    const alerts = alertsWithDupes.filter(uniqueBy('id'));
    const alertsToRead = getAlertsToRead(alerts);
    return { alerts, alertsToRead };
  }, [shipment]);

  const { firstLeg, lastLeg, currentLeg, steps, currentStep } = React.useMemo(() => {
    if (!shipment) return { firstLeg: undefined, lastLeg: undefined, currentLeg: undefined, steps: [], currentStep: undefined };

    // then we compute the firstLeg, lastLeg, currentLeg, steps, currentStep
    const firstLeg = shipment.legs[0];
    const lastLeg = shipment.legs[shipment.legs.length - 1];
    const currentLeg = getActiveLeg(shipment);
    const steps = range(1, shipment.legs.length + 2).map(String);
    const currentStep = shipment.legs.indexOf(currentLeg) + (currentLeg?.status === 'delivered' ? 2 : 1);
    return { firstLeg, lastLeg, currentLeg, steps, currentStep };
  }, [shipment]);

  const supplierReferenceIdLeg = React.useMemo(() => {
    if (!shipment) return undefined;
    const legWithSupplierReferenceId = shipment.legs.find(
      (leg) => Boolean(shipment.supplier_reference_id) && shipment.supplier_reference_id === leg.supplier_reference_id && leg.supplier_reference_type === 'shipment',
    );
    if (legWithSupplierReferenceId) return legWithSupplierReferenceId;
    const legWithSupplierReferenceTypeAsShipment = shipment.legs.find((leg) => leg.supplier_reference_type === 'shipment');
    if (legWithSupplierReferenceTypeAsShipment) return legWithSupplierReferenceTypeAsShipment;
    return undefined;
  }, [shipment]);

  // Calculate the furthest distance between any two trackers
  const furthestTrackerDistanceM = React.useMemo(() => {
    const trackers = shipment?.packages.map((pkg) => pkg.tracker).filter((tracker) => tracker?.position);
    if (!trackers || trackers.length < 2) return 0;
    let maxDistance = 0;
    for (let i = 0; i < trackers.length; i++) {
      const positionI = trackers[i]?.position;
      if (!positionI) continue;
      for (let j = i + 1; j < trackers.length; j++) {
        const positionJ = trackers[j]?.position;
        if (!positionJ) continue;
        const distance = computeDistance(stringToPosition(positionI), stringToPosition(positionJ));
        maxDistance = Math.max(maxDistance, distance);
      }
    }
    return maxDistance;
  }, [shipment]);

  const { data: driversPositions } = useSWR(currentLeg?.driver_id ? ['/device/last-positions', { user_ids: [currentLeg.driver_id] }] : null, POST, { refreshInterval: 5_000 });

  const driverPosition = React.useMemo(() => (driversPositions || []).find((dp) => dp.user_id === currentLeg?.driver_id), [driversPositions, currentLeg]);

  const markers = React.useMemo(() => {
    if (!shipment || geocodingIsMissing || !lastLeg) return [];

    const markers: Marker[] = [];

    // push first leg origin
    const markerNumber = 1;
    const markerClassName = statusToFill(firstLeg.status);
    markers.push({
      position: [Number(firstLeg.origin_position[1]), Number(firstLeg.origin_position[0])],
      icon: MarkerNumber({ className: markerClassName, number: markerNumber }),
      content: <span>{firstLeg.origin_address}</span>,
    });

    // push legs destinations
    for (const leg of shipment.legs) {
      // this check is not needed, as we are already checking for geocodingIsMissing, and it's only to silence typescript yelling about possible nulls
      if (!leg.origin_position || !leg.destination_position) {
        throw new BaseError(errorCodes.SHIPMENT_MISSING_GEOCODING, errorMessages.SHIPMENT_MISSING_GEOCODING);
      }

      const markerNumber = shipment.legs.indexOf(leg) + 2;
      const markerClassName = statusToFill(leg.status);
      markers.push({
        position: [Number(leg.destination_position[1]), Number(leg.destination_position[0])],
        icon: MarkerNumber({ className: markerClassName, number: markerNumber }),
        content: <span>{leg.destination_address}</span>,
      });
    }

    // If user is agent and current leg is not active, don't show the trackers
    if (me && env && currentLeg.status !== 'transiting') {
      return markers;
    }

    // show driver position only if there is no tracker position
    if (driverPosition && !shipment.packages.some((pkg) => pkg.tracker?.position || pkg.position)) {
      const driverPosition_1h_ago = dayjs(driverPosition.timestamp).isBefore(dayjs().subtract(1, 'hour'));
      markers.push({
        position: [Number(driverPosition.position[1]), Number(driverPosition.position[0])],
        icon: MarkerSmartphone(driverPosition_1h_ago ? 'bg-red' : 'bg-green'),
        content: (
          <div className="flex flex-col gap-1 text-center">
            <span>{t('views.shipment.receivedAt', { timestamp: dateToLocalYMDHM(driverPosition.timestamp) })}</span>
            <strong>({dayjs(driverPosition.timestamp).fromNow()})</strong>
          </div>
        ),
      });
    }

    // push package/s positions
    for (const pkg of shipment.packages) {
      const position = pkg.tracker?.position || pkg.position;

      // Filter out packages without a tracker or position (even though they are assigned, they might not have a position because MC job didn't run)
      if (!position) continue;

      const pkgId = pkg.logistics_ex_id || pkg.id;
      markers.push({
        position: stringToPosition(position).reverse() as LatLngExpression,
        icon: MarkerPackage(furthestTrackerDistanceM > MAX_DISTANCE_M ? 'bg-red' : undefined),
        content: (
          <div className="flex flex-col gap-2">
            <LegDetailItem label={t('views.shipment.labels.package')}>
              <span className="cursor-pointer text-blue hover:underline" onClick={() => setPackageDialog(pkg)}>
                <CopyableText iconSize={14} copyText={pkgId} tooltipText={pkgId}>
                  #{truncate(pkgId, 8, '')}
                </CopyableText>
              </span>
            </LegDetailItem>
            {pkg.tracker && (
              <>
                <LegDetailItem label={t('views.shipment.labels.trackerId')}>
                  <Link to={`/tracker/${pkg.tracker.id}`} className="cursor-pointer text-blue hover:underline">
                    <CopyableText iconSize={14} copyText={pkgId} tooltipText={pkgId}>
                      #{truncate(pkg.tracker?.metrics_ex_id || pkg.tracker?.id, 8, '')}
                    </CopyableText>
                  </Link>
                </LegDetailItem>
                <LegDetailItem label={t('views.shipment.labels.description')}>{pkg.tracker.description || ''}</LegDetailItem>
                <LegDetailItem label={t('views.shipment.labels.battery')}>{(pkg.tracker.battery || 0) + '%'}</LegDetailItem>
                <LegDetailItem label={t('views.shipment.labels.temperature')}>{pkg.tracker.temperature + '°C'}</LegDetailItem>
              </>
            )}
          </div>
        ),
      });
    }

    return markers;
  }, [shipment, geocodingIsMissing, firstLeg, lastLeg, furthestTrackerDistanceM, driverPosition, me, env, t, currentLeg]);

  const polylines = React.useMemo(() => {
    if (!shipment || geocodingIsMissing) return [];

    const polylines: Polyline[] = [];

    for (const leg of shipment.legs) {
      // this check is not needed, as we are already checking for geocodingIsMissing, and it's only to silence typescript yelling about possible nulls
      if (!leg.origin_position || !leg.destination_position) {
        throw new BaseError(errorCodes.SHIPMENT_MISSING_GEOCODING, errorMessages.SHIPMENT_MISSING_GEOCODING);
      }

      // opacity is passed explicitly so it overrides the Map default instead of multiplying with it
      const { hex, opacity } = LEG_COLOR_STYLE[getLegStatusColor(leg.status)];

      polylines.push({
        positions: [[...leg.origin_position].reverse(), [...leg.destination_position].reverse()] as LatLngExpression[],
        color: hex,
        opacity,
      });
    }

    return polylines;
  }, [shipment, geocodingIsMissing]);

  React.useEffect(() => {
    if (!shipment || !currentLeg || !polylines.length) return;
    const legIndex = shipment.legs.indexOf(currentLeg) + 1;
    const position = polylines[Math.max(0, legIndex - 1)].positions[0];
    setDefaultMapCenter(position);
  }, [shipment, currentLeg, polylines]);

  const brokenAddresses: string[] = React.useMemo(() => {
    if (!shipment) return [];

    const brokenAddresses: Set<string> = shipment.legs.reduce<Set<string>>((accumulator, leg) => {
      if (!leg.origin_position || isZeroPosition(leg.origin_position)) accumulator.add(leg.origin_address);
      if (!leg.destination_position || isZeroPosition(leg.destination_position)) accumulator.add(leg.destination_address);
      return accumulator;
    }, new Set());

    const uniqueBrokenAddresses = Array.from(brokenAddresses);
    return uniqueBrokenAddresses;
  }, [shipment]);

  const onStepClick = (step: string) => {
    if (isBridgeMode) return;
    const legIndex = Number(step) - 1;
    const [a, b] = polylines[Math.max(0, legIndex - 1)].positions;
    const middlePoint = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    setDefaultMapCenter(middlePoint as LatLngExpression);
    scrollToLeg(legIndex);
  };

  const scrollToLeg = React.useCallback((legIndex: string | number) => {
    const container = document.querySelector('#legs') as HTMLElement;
    const element = document.querySelector(`[data-leg="${String(legIndex)}"]`) as HTMLElement;
    if (!container || !element) return;
    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    container.scrollTop = elementRect.top - containerRect.top + container.scrollTop;
  }, []);

  React.useEffect(() => {
    if (!shipment || !currentLeg) return;
    const legIndex = shipment.legs.indexOf(currentLeg) + 1;
    if (legIndex > 1) scrollToLeg(legIndex);
  }, [shipment, currentLeg, scrollToLeg]);

  const onTrackerAssign = (pkg: Package) => () => setTrackerDialog(pkg);
  const onTrackerAssigned = async () => {
    setTrackerDialog(null);
    await mutateShipment();
  };

  const onDriverAssign = (leg: TLeg) => () => setDriverDialog(leg);
  const onDriverAssigned = async () => {
    setDriverDialog(null);
    await mutateShipment();
  };

  const onSupplierAssign = (leg: TLeg) => () => setSupplierDialog(leg);
  const onSupplierAssigned = async () => {
    setSupplierDialog(null);
    await mutateShipment();
  };

  const onOpenHistoryDialog = () => setHistoryDialog(true);
  const onOpenLegEditDialog = (leg: TLeg) => () => setLegEditDialog(leg);
  const onCloseHistoryDialog = () => setHistoryDialog(false);

  const onOpenReferenceIdDialog = () => setReferenceIdDialog(true);
  const onCloseReferenceIdDialog = () => setReferenceIdDialog(false);

  const onClosePackageDialog = () => setPackageDialog(null);
  const onCloseDriverDialog = () => setDriverDialog(null);
  const onCloseSupplierDialog = () => setSupplierDialog(null);

  const onOpenUploadDialog = (legs: TLeg[]) => () => setUploadDialog(legs);
  const onCloseUploadDialog = async () => {
    setUploadDialog(null);
    await mutateShipment();
  };

  const onCloseLegEditDialog = async () => {
    setLegEditDialog(null);
    await mutateShipment();
  };

  const onReferenceIdSaved = async () => {
    await mutateShipment();
  };

  const onDeleteDocument = async () => {
    await mutateShipment();
  };

  const onGeneratePublicLink = async () => {
    const result = await createPublicLinkMutation.trigger({});
    setGeneratedPublicLinkId(result.id);
    setGeneratedPublicUrl(result.public_url);
  };

  const onRevokePublicLink = async () => {
    if (!generatedPublicLinkId) return;
    await revokePublicLinkMutation.trigger({ linkId: generatedPublicLinkId });
    setGeneratedPublicLinkId(null);
    setGeneratedPublicUrl(null);
    setCopiedPublicLink(false);
  };

  const onCopyPublicLink = async () => {
    if (!generatedPublicUrl) return;
    await navigator.clipboard.writeText(generatedPublicUrl);
    setCopiedPublicLink(true);
    window.setTimeout(() => setCopiedPublicLink(false), 1200);
  };

  React.useEffect(() => {
    if (!activePublicLink) return;
    setGeneratedPublicLinkId(activePublicLink.id);
    setGeneratedPublicUrl(activePublicLink.public_url);
  }, [activePublicLink]);

  const confirmMutation = React.useCallback(
    (mutation: any, payload: any = {}) =>
      async () => {
        if (!(await confirm('Do you confirm the operation?'))) return;
        try {
          await mutation.trigger(payload);
          await alert('Operation completed.');
        } catch {
          await alert('Operation failed.');
        }
      },
    [confirm, alert],
  );

  const status = React.useMemo(() => {
    if (!shipment) return undefined;
    return shipment.status;
  }, [shipment]);

  const shipmentStatusColor = React.useMemo(() => {
    if (!shipment || !status) return 'grey';
    if (env && isGranted(me, 'ViewAdminLayout', env.SUPERADMIN_GROUP_ID)) {
      return getStatusColor(status);
    }
    return getStatusColor(status);
  }, [shipment, status, me, env]);

  const shipmentDelayedColor = React.useMemo(() => {
    if (!shipment) return '';

    return getShipmentDelayedColor(shipment);
  }, [shipment]);

  const shipmentDelayedTooltipText = React.useMemo(() => {
    if (!shipment) return '';
    const currentLeg = getActiveLeg(shipment);
    if (!currentLeg) return '';

    if (currentLeg.delayed) {
      return t('views.shipment.tooltips.delayedShipment', {
        status: shipment.status === 'delivered' ? t('views.shipment.tooltips.wasDelivered') : t('views.shipment.tooltips.is'),
      });
    }

    // Next ETA is less than 30 minutes means the shipment is about to be delayed
    const nextETA = getNextETA(shipment);
    if (nextETA && nextETA < 30) return t('views.shipment.tooltips.nextETA', { minutes: nextETA });

    return '';
  }, [t, shipment]);

  const is_missing_pod = React.useMemo(() => {
    if (!shipment) return false;
    return isPastLegMissingPodDatetime(shipment);
  }, [shipment]);

  const originInfo: { name: string | null; address: string | null } = React.useMemo(() => {
    if (!me || !shipment) return { name: null, address: null };
    if (isBridgeMode) return { name: shipment.collection_city || null, address: shipment.collection_country || null };
    if (env && isGranted(me, 'ViewAdminLayout', env.SUPERADMIN_GROUP_ID)) return { name: shipment.shipper.name, address: shipment.shipper.address };
    const firstLeg = shipment.legs[0];
    return { name: firstLeg.origin_address_name, address: firstLeg.origin_address };
  }, [me, shipment, env, isBridgeMode]);

  const destinationInfo: { name: string | null; address: string | null } = React.useMemo(() => {
    if (!me || !shipment) return { name: null, address: null };
    if (isBridgeMode) return { name: shipment.delivery_city || null, address: shipment.delivery_country || null };
    const lastLeg = shipment.legs[shipment.legs.length - 1];
    if (env && isGranted(me, 'ViewAdminLayout', env.SUPERADMIN_GROUP_ID)) return { name: shipment.consignee.name, address: shipment.consignee.address };
    else return { name: lastLeg.destination_address_name, address: lastLeg.destination_address };
  }, [me, shipment, env, isBridgeMode]);

  const is_shipment_silenced = React.useMemo(() => {
    if (!userSettings) return false;
    if (!shipment) return false;
    return userSettings.shipments_silenced?.includes(shipment.id);
  }, [shipment, userSettings]);

  const onToggleShipmentSilenced = React.useCallback(async () => {
    if (!userSettings) return;
    if (!shipment) return;
    await setUserSettingsMutation.trigger({
      shipments_silenced: is_shipment_silenced ? userSettings.shipments_silenced?.filter((id) => id !== shipment.id) : [...userSettings.shipments_silenced, shipment.id],
    });
    await mutateUserSettings();
  }, [shipment, userSettings, setUserSettingsMutation, is_shipment_silenced, mutateUserSettings]);

  const updatableLegs = React.useMemo(() => {
    if (!me) return [];
    if (!shipment) return [];
    return shipment.legs.filter((leg) => isGranted(me, 'LegUpdate', leg.id) || canUpdateLeg(leg));
  }, [me, shipment, canUpdateLeg]);

  const userCanUpdateAllLegs = React.useMemo(() => {
    if (!me) return false;
    if (!shipment) return false;
    return updatableLegs.length === shipment.legs.length;
  }, [me, shipment, updatableLegs]);

  const hasDatalogger = React.useMemo(() => {
    if (!shipment) return false;
    return shipment.packages.some((pkg) => Boolean(pkg.tracker || pkg.last_tracker));
  }, [shipment]);

  if (!shipment || !firstLeg || !currentLeg || !lastLeg) return <Spinner centered={true} />;

  const canUpdateShipment = !isBridgeMode && isGranted(me, 'ShipmentUpdate', shipment.id);

  return (
    <main className="view-container">
      {trackerDialog && (
        <TrackerAssignmentDialog
          key={trackerDialog.id}
          shipment={shipment}
          pkg={trackerDialog}
          isOpen={!!trackerDialog}
          onClose={() => setTrackerDialog(null)}
          onSave={onTrackerAssigned}
        />
      )}
      {driverDialog && <DriverAssignmentDialog key={driverDialog.id} legs={[driverDialog]} isOpen={!!driverDialog} onClose={onCloseDriverDialog} onSave={onDriverAssigned} />}
      {supplierDialog && (
        <SupplierAssignmentDialog
          key={supplierDialog.id}
          shipment={shipment}
          leg={supplierDialog}
          isOpen={!!supplierDialog}
          onClose={onCloseSupplierDialog}
          onSave={onSupplierAssigned}
        />
      )}
      {uploadDialog && (
        <DocumentUploadDialog
          key={uploadDialog[0].id || shipment.id}
          shipment={shipment}
          legs={uploadDialog}
          isOpen={!!uploadDialog}
          onClose={onCloseUploadDialog}
          onSave={onCloseUploadDialog}
        />
      )}

      {historyDialog && <ShipmentHistory shipment={shipment} isOpen={true} onClose={onCloseHistoryDialog} />}
      {packageDialog && <PackageDetail shipment={shipment} pkg={packageDialog} isOpen={!!packageDialog} onClose={onClosePackageDialog} />}
      {legEditDialog && (
        <LegEditDialog key={legEditDialog.id || shipment.id} leg={legEditDialog} isOpen={!!legEditDialog} onClose={onCloseLegEditDialog} onSave={onCloseLegEditDialog} />
      )}
      {supplierReferenceIdLeg && referenceIdDialog && (
        <ReferenceIdDialog shipperReferenceIdLeg={supplierReferenceIdLeg} onClose={onCloseReferenceIdDialog} isOpen={referenceIdDialog} onSave={onReferenceIdSaved} />
      )}
      {instructionsDialog && (
        <ShipmentInstructionsEditDialog
          shipment={shipment}
          isOpen={instructionsDialog}
          onClose={() => setInstructionsDialog(false)}
          onSave={async () => {
            setInstructionsDialog(false);
            await mutateShipment();
          }}
        />
      )}

      <section className="mb-6 flex flex-row items-center gap-6">
        <div className="flex flex-row gap-1">
          <span className="text-nowrap text-grey-label">{t('views.shipment.labels.orderNumber')}:</span>
          <strong className="w-max truncate">{shipment.logistics_ex_id}</strong>
        </div>
        {!isBridgeMode && (
          <div className="flex flex-row items-center gap-1">
            <span className="text-nowrap text-grey-label">{t('views.shipment.labels.owner')}:</span>
            <strong className="w-max truncate">
              <Link to={`/user/${shipment.owner_id}`} className="text-blue hover:underline">
                {shipment.owner.name}
              </Link>
            </strong>
          </div>
        )}
        {!isBridgeMode && (
          <div className="flex flex-row items-center gap-1">
            <span className="text-nowrap text-grey-label">{t('views.shipment.labels.customer')}:</span>
            <CopyableText iconSize={14} copyText={shipment.customer.id} tooltipText={'Click to copy the Customer ID'}>
              <strong className="w-max truncate">{shipment.customer.name}</strong>
            </CopyableText>
          </div>
        )}
        {!isBridgeMode && supplierReferenceIdLeg && (
          <div className="flex flex-row items-center gap-1">
            <span className="text-nowrap text-grey-label">{t('views.shipment.labels.supplierReferenceId')}:</span>
            <strong className="w-max truncate">{shipment.supplier_reference_id || '-'}</strong>
            {(isGranted(me, 'ShipmentSetSupplierId', shipment.owner_id) || isGranted(me, 'ShipmentSetSupplierId', shipment.id)) && (
              <span onClick={onOpenReferenceIdDialog} className="-mt-1 -ml-1 p-1 text-blue hover:cursor-pointer hover:bg-gray-100">
                <Tooltip text="Set Supplier reference ID for the shipment.">
                  <IEdit size={17} />
                </Tooltip>
              </span>
            )}
          </div>
        )}

        <div className="ml-auto flex grow flex-row justify-end gap-1">
          {shipment.labels.map((label) => (
            <Tag key={label} className="!p-2 grey">
              {label}
            </Tag>
          ))}
        </div>

        <div className="ml-auto flex flex-row items-center gap-2">
          <span className="text-nowrap text-grey-label">{t('views.shipment.labels.status')}:</span>
          <Tag className={cx('!p-2 capitalize', shipmentStatusColor)}>{status || '-'}</Tag>
          {shipmentDelayedTooltipText && (
            <Tooltip text={shipmentDelayedTooltipText}>
              <IClock className={shipmentDelayedColor} size={20} />
            </Tooltip>
          )}

          {is_missing_pod && (
            <Tooltip text={t('tooltips.missingPodDatetime')}>
              <IWarning className="text-yellow" size={20} />
            </Tooltip>
          )}

          {!isBridgeMode && (
            <Tooltip text={t('tooltips.exportToXlsx')}>
              <Button className="!p-0 !w-auto" onClick={exportToXLSX}>
                <IFileSpreadsheet size={20} className="blue" />
              </Button>
            </Tooltip>
          )}
          {!isBridgeMode && (
            <Tooltip text={is_shipment_silenced ? t('views.shipment.tooltips.unmuteShipment') : t('views.shipment.tooltips.muteShipment')}>
              <Button className="!p-0 !w-auto" onClick={onToggleShipmentSilenced}>
                {is_shipment_silenced ? <IBellSilent size={20} className="grey" /> : <IBell size={20} className="blue" />}
              </Button>
            </Tooltip>
          )}
        </div>

        {!isBridgeMode && showOperatorButtons && (
          <div className="ml-auto">
            <Button
              className="blue"
              LeftIcon={IRefresh}
              onClick={confirmMutation(reassignShipmentMutation, { id: shipment.id })}
              disabled={reassignShipmentMutation.isMutating}
              loading={reassignShipmentMutation.isMutating}
            >
              Reassign legs
            </Button>
          </div>
        )}
      </section>

      <div className={cx('grid gap-6', isBridgeMode ? 'grid-cols-1' : 'grid-cols-2')}>
        <div className="mb-8 flex flex-col gap-6">
          <section className="paper !px-6 flex flex-col gap-3">
            <h3 className="body2 pb-3">{t('views.shipment.sections.tracking.title')}</h3>
            <div className="relative h-[300px] place-content-between place-items-center">
              {!geocodingIsMissing && (
                <React.Suspense fallback={<Spinner centered={true} />}>
                  <MapComponent className="min-h-[300px] w-full" defaultCenter={defaultMapCenter} markers={markers} polylines={polylines} disablePopups={isBridgeMode} />
                </React.Suspense>
              )}
              {geocodingIsMissing && (
                <div className="flex min-h-[300px] w-full flex-col place-content-center place-items-center bg-gray-400 px-8 py-2 text-left text-white">
                  <IWarning className="mb-4 fill-yellow-500" size={60} />
                  <span>{t('views.shipment.sections.tracking.geocodingError.title')}</span>
                  <ul className="list-inside list-decimal">
                    {brokenAddresses.map((address, index) => (
                      <li key={index}>{address}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            {isBridgeMode ? (
              <div className="body3 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:gap-6">
                  <span className="flex flex-col gap-1">
                    <span className="text-grey-label">{t('views.shipment.labels.pickup')}</span>
                    <span>
                      <strong className="block">{originInfo.name}</strong>
                      {originInfo.address}
                    </span>
                  </span>
                  <span className="flex flex-col gap-1">
                    <span className="text-grey-label">{t('views.shipment.labels.expectedPickupTime')}</span>
                    <Datetime timezone={firstLeg.origin_tz || getRuntimeTimezone()} truncateRegion={true} layout={'y'} rangeFrom={firstLeg.origin_expected_from_datetime}>
                      {firstLeg.origin_expected_datetime}
                    </Datetime>
                  </span>
                </div>
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:gap-6">
                  <span className="flex flex-col gap-1">
                    <span className="text-grey-label">{t('views.shipment.labels.delivery')}</span>
                    <span>
                      <strong className="block">{destinationInfo.name}</strong>
                      {destinationInfo.address}
                    </span>
                  </span>
                  <span className="flex flex-col gap-1">
                    <span className="text-grey-label">{t('views.shipment.labels.expectedDeliveryTime')}</span>
                    <Datetime timezone={lastLeg.destination_tz || getRuntimeTimezone()} truncateRegion={true} layout={'y'} rangeFrom={lastLeg.destination_expected_from_datetime}>
                      {lastLeg.destination_expected_datetime}
                    </Datetime>
                  </span>
                </div>
              </div>
            ) : (
              <div className="body3 flex flex-row place-content-between gap-3">
                <span className="flex flex-row gap-2">
                  <span className="text-grey-label">{t('views.shipment.labels.pickup')}:</span>
                  <span>
                    <strong className="block">{originInfo.name}</strong>
                    {originInfo.address}
                  </span>
                </span>
                <span className="flex flex-row gap-2">
                  <span className="text-grey-label">{t('views.shipment.labels.delivery')}:</span>
                  <span>
                    <strong className="block">{destinationInfo.name}</strong>
                    {destinationInfo.address}
                  </span>
                </span>
              </div>
            )}

            {!isBridgeMode && <Stepper steps={steps} currentStep={String(currentStep)} onStepClick={onStepClick} currentLegStatus={currentLeg.status} />}

            {!isBridgeMode && (
              <div className="body3 flex flex-row place-content-between">
                <span className="flex flex-col items-center gap-2">
                  <span className="text-grey-label">{t('views.shipment.labels.expectedPickupTime')}</span>
                  <Datetime timezone={firstLeg.origin_tz || getRuntimeTimezone()} truncateRegion={true} layout={'y'} rangeFrom={firstLeg.origin_expected_from_datetime}>
                    {firstLeg.origin_expected_datetime}
                  </Datetime>
                </span>
                <span className="flex flex-col items-center gap-2">
                  <span className="text-grey-label">{t('views.shipment.labels.expectedDeliveryTime')}</span>
                  <Datetime timezone={lastLeg.destination_tz || getRuntimeTimezone()} truncateRegion={true} layout={'y'} rangeFrom={lastLeg.destination_expected_from_datetime}>
                    {lastLeg.destination_expected_datetime}
                  </Datetime>
                </span>
              </div>
            )}

            <hr />

            <div className="flex w-full flex-col items-start gap-3">
              {!isBridgeMode && env && isGranted(me, 'DocumentCreate', `${env.SUPERADMIN_GROUP_ID}/*`) && userCanUpdateAllLegs && (
                <Button LeftIcon={ICamera} className="blue-outlined relative w-max gap-2" onClick={onOpenUploadDialog(shipment.legs)}>
                  <div className="flex flex-row items-center gap-2">
                    <span>{t('views.shipment.buttons.uploadDoc')}</span>
                    <Tooltip text={t('views.shipment.tooltips.uploadDocAllLegs')}>
                      <IInfo className="grey" size={16} />
                    </Tooltip>
                  </div>
                </Button>
              )}
              {!isBridgeMode && (
                <Button LeftIcon={IHistory} className="blue-outlined w-max" onClick={onOpenHistoryDialog}>
                  {t('views.shipment.buttons.history')}
                </Button>
              )}
              {isBridgeMode && canGeneratePublicLink && (
                <div className="flex w-full flex-wrap items-center gap-3">
                  <Button
                    className="blue-outlined w-max"
                    onClick={onGeneratePublicLink}
                    disabled={createPublicLinkMutation.isMutating}
                    loading={createPublicLinkMutation.isMutating}
                  >
                    Generate Public Link
                  </Button>
                  {generatedPublicUrl && (
                    <Button className="blue-outlined w-max" onClick={onCopyPublicLink}>
                      {copiedPublicLink ? 'Copied' : 'Copy Link'}
                    </Button>
                  )}
                  {generatedPublicUrl && (
                    <Button
                      className="blue-outlined w-max"
                      onClick={onRevokePublicLink}
                      disabled={revokePublicLinkMutation.isMutating}
                      loading={revokePublicLinkMutation.isMutating}
                    >
                      Revoke Public Link
                    </Button>
                  )}
                </div>
              )}
            </div>
          </section>

          <section className="paper !px-6 !py-4">
            <h3 className="body2 pb-3">{t('views.shipment.sections.packages.title')}</h3>
            <div
              className={cx(
                'grid place-content-center place-items-center gap-3 py-4 text-grey-label',
                packagesGridCols(isBridgeMode, Boolean(env && isGranted(me, 'ViewAdminLayout', env.SUPERADMIN_GROUP_ID))),
              )}
            >
              {!isBridgeMode && <span className="justify-self-start">{t('labels.id')}</span>}
              <span className="justify-self-start">{t('views.shipment.labels.description')}</span>
              <span className="">{t('views.shipment.labels.quantity')}</span>
              {SHOW_DATALOGGER_COLUMN && <span className="">{t('views.shipment.labels.tracker')}</span>}
              {env && isGranted(me, 'ViewAdminLayout', env.SUPERADMIN_GROUP_ID) && <span className="">{t('views.shipment.sections.packages.columns.temperature')}</span>}
              {env && isGranted(me, 'ViewAdminLayout', env.SUPERADMIN_GROUP_ID) && <span className="">{t('views.shipment.sections.packages.columns.range')}</span>}
              {env && isGranted(me, 'ViewAdminLayout', env.SUPERADMIN_GROUP_ID) && <span className="">{t('views.shipment.sections.packages.columns.alerts')}</span>}
            </div>
            <hr />
            {shipment.packages.map((pkg: Package) => (
              <div
                key={pkg.id}
                className={cx(
                  'grid place-content-center place-items-center gap-3 border-b border-b-grey-border',
                  packagesGridCols(isBridgeMode, Boolean(env && isGranted(me, 'ViewAdminLayout', env.SUPERADMIN_GROUP_ID))),
                )}
              >
                {!isBridgeMode && (
                  <span
                    className={cx(
                      'max-w-full justify-self-start truncate py-3 uppercase',
                      isBridgeMode ? 'cursor-default text-current' : 'cursor-pointer text-blue hover:underline',
                    )}
                    onClick={isBridgeMode ? undefined : () => setPackageDialog(pkg)}
                  >
                    {pkg.logistics_ex_id}
                  </span>
                )}
                <span className="justify-self-start">{pkg.description || '-'}</span>
                <span>{pkg.quantity || '-'}</span>
                {SHOW_DATALOGGER_COLUMN && (
                  <span className="max-w-full truncate py-3">
                    {shipment.status === 'delivered' && pkg.last_tracker && (
                      <div className="blue flex items-center justify-center gap-1">
                        <Tooltip text={`${pkg.last_tracker.metrics_ex_id} (${pkg.last_tracker?.description || ''})`}>
                          {isBridgeMode ? (
                            <span className="uppercase">{pkg.last_tracker.description || pkg.last_tracker.metrics_ex_id}</span>
                          ) : (
                            <NavLink to={`/tracker/${pkg.last_tracker.id}`} className="p-1 hover:bg-gray-100">
                              <span className="uppercase">{pkg.last_tracker.description || pkg.last_tracker.metrics_ex_id}</span>
                            </NavLink>
                          )}
                        </Tooltip>
                      </div>
                    )}
                    {shipment.status === 'delivered' && !pkg.last_tracker && 'N/A'}
                    {shipment.status !== 'delivered' && (
                      <>
                        {!pkg.tracker && <span>N/A</span>}
                        {pkg.tracker && (
                          <div className="blue flex items-center justify-center gap-1">
                            <Tooltip text={`${pkg.tracker.metrics_ex_id} (${pkg.tracker?.description || ''})`}>
                              {isBridgeMode ? (
                                <span className="uppercase">{pkg.tracker.description || pkg.tracker.metrics_ex_id}</span>
                              ) : (
                                <NavLink to={`/tracker/${pkg.tracker.id}`} className="p-1 hover:bg-gray-100">
                                  <span className="uppercase">{pkg.tracker.description || pkg.tracker.metrics_ex_id}</span>
                                </NavLink>
                              )}
                            </Tooltip>
                            {!isBridgeMode && env && canAssignTracker(pkg) && (
                              <span onClick={onTrackerAssign(pkg)} className="p-1 hover:cursor-pointer hover:bg-gray-100">
                                <Tooltip text={t('views.shipment.sections.packages.actions.reassignTracker')}>
                                  <IEdit size={17} />
                                </Tooltip>
                              </span>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </span>
                )}
                {env && isGranted(me, 'ViewAdminLayout', env.SUPERADMIN_GROUP_ID) && (
                  <span className="max-w-full truncate py-3">{pkg.temperature ? pkg.temperature + 'ºC' : '-'}</span>
                )}
                {env && isGranted(me, 'ViewAdminLayout', env.SUPERADMIN_GROUP_ID) && (
                  <span className="max-w-full truncate py-3">
                    {pkg.th_max_temperature && pkg.th_min_temperature ? `${pkg.th_min_temperature}ºC / ${pkg.th_max_temperature}ºC` : '-'}
                  </span>
                )}
                {env && isGranted(me, 'ViewAdminLayout', env.SUPERADMIN_GROUP_ID) && (
                  <span className="py-3">
                    {!alerts.length && '0/0'}
                    {!!alerts.length && (
                      <Tag
                        className={cx('tag-alerts cursor-pointer', pkg.alerts.filter((alert) => !alert.archived_at).length > 0 ? 'red' : 'grey')}
                        onClick={isBridgeMode ? undefined : () => setPackageDialog(pkg)}
                      >
                        {t('labels.alertsUnreadOfTotal', { unread: alertsToRead.length, total: alerts.length })}
                      </Tag>
                    )}
                  </span>
                )}
              </div>
            ))}
          </section>

          {(shipment.instructions || canUpdateShipment) && (
            <section className="paper px-6! py-4!">
              <div className="flex flex-row items-center justify-between pb-3">
                <h3 className="body2">{t('views.shipment.labels.instructions')}</h3>
                {canUpdateShipment && (
                  <span onClick={() => setInstructionsDialog(true)} className="p-1 text-blue hover:cursor-pointer hover:bg-gray-100">
                    <Tooltip text={t('views.shipment.tooltips.editInstructions')}>
                      <IEdit size={17} />
                    </Tooltip>
                  </span>
                )}
              </div>
              {shipment.instructions && <span className="wrap-anywhere whitespace-pre-line">{shipment.instructions}</span>}
              {!shipment.instructions && <span className="body3 text-grey-label">{t('views.shipment.labels.noInstructions')}</span>}
            </section>
          )}
          {isBridgeMode && hasDatalogger && (
            <section className="paper !px-6 !py-4">
              <h3 className="body2 pb-3">Temp Report</h3>
              <div className="body3 text-grey-label">Real-time IoT Hub temperature data will be available here in a future release.</div>
            </section>
          )}
        </div>

        {!isBridgeMode && (
          <section id="legs" className="flex h-max max-h-[calc(100vh_-_200px)] flex-col overflow-auto scroll-smooth border border-slate-200 bg-white px-6 py-0">
            {shipment.legs.map((leg, index) => (
              <Leg
                key={index}
                index={index}
                leg={leg}
                role={me.role}
                onSupplierAssign={onSupplierAssign}
                onDriverAssign={onDriverAssign}
                onOpenUploadDialog={onOpenUploadDialog}
                onOpenLegEditDialog={onOpenLegEditDialog}
                onDeleteDocument={onDeleteDocument}
                bridgeMode={isBridgeMode}
                permissions={{
                  canAssignSupplier: canAssignSupplier(leg),
                  canAssignDriver: canAssignDriver(leg),
                  canEditLeg: canUpdateLeg(leg),
                }}
              />
            ))}
          </section>
        )}
      </div>
    </main>
  );
};
